import { Delete, MoreHoriz } from '@mui/icons-material';
import { ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import { useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useDeleteRoles } from '~/api/roles';
import { IconButton } from '~/components/IconButton';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import { mergeGridActionProps, type GridActionCellProps } from '~/components/table';
import { useAppContext } from '~/contexts';
import { useConfirmDialog } from '~/hooks/dialogs';
import type { RoleList__Role } from '../RoleList.generated';

export interface RoleMenuProps extends GridActionCellProps {
  networkRole: RoleList__Role;
}

export const RoleMenu = ({ networkRole, ...cellProps }: RoleMenuProps) => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();

  const [deleteRoles] = useDeleteRoles();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const [confirmDelete, confirmDeleteProps] = useConfirmDialog();

  const close = () => setAnchorEl(null);

  const menuButtonProps = mergeGridActionProps(
    { onClick: (event: MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget) },
    cellProps,
  );

  return (
    <>
      <IconButton label={t('pages.roles.list.actions_tooltip')} {...menuButtonProps} size="small">
        <MoreHoriz />
      </IconButton>

      <Menu anchorEl={anchorEl} onClose={close} open={anchorEl != null}>
        <MenuItem
          onClick={async () => {
            close();
            if (!(await confirmDelete())) return;
            await deleteRoles({
              variables: { input: { networkId: currentNetwork.id, roleIds: [networkRole.id] } },
            });
          }}
        >
          <ListItemIcon>
            <Delete color="error" />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ color: 'error' }}>
            {t('common.dialogs.delete')}
          </ListItemText>
        </MenuItem>
      </Menu>

      <ConfirmDialog
        {...confirmDeleteProps}
        cancel={t('common.buttons.cancel')}
        confirm={t('common.dialogs.permanently_delete')}
        deleteConfirm
        prompt={t('pages.roles.dialogs.delete.prompt')}
        title={t('pages.roles.dialogs.delete.title')}
      />
    </>
  );
};
